import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Trophy, Users, Calendar, Star } from 'lucide-react';
import { fetchRaidMythicBannerState } from '../services/raidMythicProgressApi';
import { getCurrentTierProgress } from '../data/raidProgressData';

const currentHeroicProgress = getCurrentTierProgress('Heroic');

const Home: React.FC = () => {
  const [mythicProgressDisplay, setMythicProgressDisplay] = useState(
    () => getCurrentTierProgress('Mythic') || '?'
  );

  useEffect(() => {
    let cancelled = false;
    fetchRaidMythicBannerState().then((res) => {
      if (cancelled || !res.ok) return;
      setMythicProgressDisplay(
        `${res.data.progress}/${res.data.totalBosses}`
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(
    () => [
      { icon: Trophy, label: 'Mythic Progress', value: mythicProgressDisplay },
      { icon: Users, label: 'Active Members', value: '70+' },
      { icon: Calendar, label: 'Raid Days', value: '2/week' },
      {
        icon: Star,
        label: 'Heroic Cleared',
        value: currentHeroicProgress || '?',
      },
    ],
    [mythicProgressDisplay]
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Replace gradient with image background */}
        <div className="absolute inset-0">
          <img
            src="/images/Xalatath-banner.jpg"
            alt="Xalatath Banner"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.5)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/80" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="gradient-text">Welcome to Just Vibes</span>
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary mb-8 max-w-3xl mx-auto">
            Formed at the start of Dragonflight, we're a 6-hour mid-core guild pursuing Cutting Edge while fostering the kind of community that makes raid nights, alt runs, and Mythic+ worth showing up for.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/recruitment"
                className="inline-flex items-center px-8 py-4 btn-neon rounded-lg transform hover:scale-105"
              >
                Join Our Guild
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 glass-enhanced text-text-primary font-semibold rounded-lg hover:bg-surface-light transition-all duration-200"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-text-primary mb-2">{stat.value}</div>
                <div className="text-text-secondary">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="gradient-text">Why Choose Just Vibes?</span>
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Good vibes, steady progression, and plenty of keys. We take Mythic raiding seriously without taking ourselves too seriously.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Mid-Core Raiding',
                description: '6 hours per week of focused raiding with a fun, relaxed atmosphere.',
              },
              {
                title: 'Active Community',
                description: 'Alt runs, M+ groups, and lots of activities on off days.',
              },
              {
                title: 'Cutting Edge Goals',
                description: 'Striving to reach CE in coming seasons with consistent progression.',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="glass p-8 rounded-xl hover:bg-surface-light transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-text-secondary">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Replace gradient with image background */}
        <div className="absolute inset-0">
          <img
            src="/images/Dimensius.jpg"
            alt="Dimensius Banner"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.5)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/80" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="gradient-text">Ready to Join the Adventure?</span>
            </h2>
            <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
              Whether you're a seasoned raider or just starting your journey, 
              we have a place for you in our community.
            </p>
            <Link
              to="/recruitment"
              className="inline-flex items-center px-8 py-4 btn-neon rounded-lg transform hover:scale-105"
            >
              Apply Now
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 